import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { FileUp, Loader2, CheckCircle2, AlertTriangle, CloudDownload } from "lucide-react";
import { toast } from "sonner";

const TONES = {
  slate: "bg-slate-50 border-slate-200 text-slate-800",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  amber: "bg-amber-50 border-amber-200 text-amber-800",
  rose: "bg-rose-50 border-rose-200 text-rose-800",
  purple: "bg-purple-50 border-purple-200 text-purple-800",
};

const Stat = ({ label, value, tone = "slate" }) => (
  <div className={`rounded-lg border p-3 text-center ${TONES[tone]}`}>
    <div className="text-2xl font-black" style={{ fontFamily: "Nunito" }}>{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
  </div>
);

export const ScoutingImportDialog = ({ open, onOpenChange, onImported }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sheetUrl, setSheetUrl] = useState("");
  const [sheetLoading, setSheetLoading] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/scouting-market/google-sheet-url")
        .then((r) => setSheetUrl(r.data.url || ""))
        .catch(() => {});
    }
  }, [open]);

  const reset = () => setPreview(null);

  const pick = () => inputRef.current?.click();

  const pollStatus = async (importId, targets) => {
    for (let i = 0; i < 240; i++) {
      await new Promise((r) => setTimeout(r, 2500));
      try {
        const { data } = await api.get(`/scouting-market/import/${importId}/status`);
        if (data.status === "error") {
          const e = new Error(data.error_summary || "Error al procesar el archivo");
          e.isImportError = true;
          throw e;
        }
        if (targets.includes(data.status)) return data;
      } catch (err) {
        if (err.isImportError) throw err;
        // errores de red puntuales: seguir esperando
      }
    }
    throw new Error("Tiempo de espera agotado. Revisa el historial de importaciones.");
  };

  const startPreview = async (postFn) => {
    setUploading(true);
    try {
      const { data } = await postFn();
      const doc = await pollStatus(data.import_id, ["vista_previa"]);
      setPreview({ ...(doc.preview || {}), import_id: doc.id });
    } catch (err) {
      toast.error(err.isImportError || !err.response ? err.message : formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const fromSheet = async () => {
    if (!sheetUrl.trim()) {
      toast.error("Pega la URL del Google Sheet");
      return;
    }
    setSheetLoading(true);
    try {
      const { data } = await api.post("/scouting-market/import/google-sheet",
        { url: sheetUrl.trim() }, { timeout: 60000 });
      const doc = await pollStatus(data.import_id, ["vista_previa"]);
      setPreview({ ...(doc.preview || {}), import_id: doc.id });
    } catch (err) {
      toast.error(err.isImportError || !err.response ? err.message : formatApiError(err));
    } finally {
      setSheetLoading(false);
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Solo se aceptan archivos XLSX");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error("El archivo supera los 15 MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    await startPreview(() => api.post("/scouting-market/import/preview", fd, {
      headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
    }));
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      await api.post(`/scouting-market/import/${preview.import_id}/confirm`, {}, { timeout: 60000 });
      await pollStatus(preview.import_id, ["confirmada"]);
      toast.success("Importación aplicada correctamente");
      reset();
      onOpenChange(false);
      onImported?.();
    } catch (err) {
      toast.error(err.isImportError || !err.response ? err.message : formatApiError(err));
    } finally {
      setConfirming(false);
    }
  };

  const cancel = async () => {
    if (preview) {
      try { await api.post(`/scouting-market/import/${preview.import_id}/cancel`); } catch { /* noop */ }
    }
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) cancel(); else onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="scouting-import-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Nunito" }}>Actualizar scouting</DialogTitle>
          <DialogDescription>
            Carga el archivo XLSX (hojas "Base Unificada" y "Temporadas RFFM").
            Verás una vista previa y nada se guardará hasta que confirmes.
          </DialogDescription>
        </DialogHeader>

        <input ref={inputRef} type="file" accept=".xlsx" className="hidden" onChange={onFile} data-testid="scouting-import-file-input" />

        {!preview ? (
          <div className="py-4 space-y-5">
            <div className="grid place-items-center">
              <Button onClick={pick} disabled={uploading || sheetLoading} className="bg-purple-700 hover:bg-purple-800 text-white font-bold" data-testid="scouting-import-pick-btn">
                {uploading
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analizando archivo...</>
                  : <><FileUp className="h-4 w-4 mr-2" /> Seleccionar archivo XLSX</>}
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[10px] uppercase tracking-widest text-slate-400">o desde Google Sheets</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
            <div className="space-y-2">
              <Input
                value={sheetUrl}
                onChange={(e) => setSheetUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                className="text-xs"
                data-testid="scouting-sheet-url-input"
              />
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-[11px] text-slate-400">
                  Descarga puntual bajo demanda (el enlace debe ser público). Sin sincronización automática.
                </p>
                <Button
                  variant="outline"
                  onClick={fromSheet}
                  disabled={uploading || sheetLoading}
                  className="border-purple-300 text-purple-700 hover:bg-purple-50 font-bold"
                  data-testid="scouting-sheet-import-btn"
                >
                  {sheetLoading
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Descargando...</>
                    : <><CloudDownload className="h-4 w-4 mr-2" /> Importar desde Google Sheets</>}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="scouting-import-preview">
            <div className="text-sm text-slate-600">
              Archivo: <b>{preview.filename}</b> · {preview.total_rows} filas leídas · {preview.season_rows} temporadas
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              <Stat label="Nuevos" value={preview.new_players} tone="emerald" />
              <Stat label="Se actualizarán" value={preview.updated_players} tone="amber" />
              <Stat label="Sin cambios" value={preview.unchanged_players} tone="slate" />
              <Stat label="Conflictos" value={preview.conflicts} tone="rose" />
              <Stat label="Rechazados" value={preview.rejected_rows} tone="rose" />
              <Stat label="Coincidencias" value={preview.updated_players + preview.unchanged_players} tone="purple" />
              <Stat label="Total esperado" value={preview.expected_total} tone="purple" />
            </div>

            {preview.detail_rows > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="Actas leídas" value={preview.detail_rows} tone="purple" />
                <Stat label="Actas nuevas" value={preview.detail_new} tone="emerald" />
                <Stat label="Actas a actualizar" value={preview.detail_update} tone="amber" />
                <Stat label="Sin minutos publicados" value={preview.detail_unpublished} tone="slate" />
              </div>
            )}

            {preview.new_samples?.length > 0 && (
              <div className="text-xs text-slate-500">
                <b className="text-emerald-700">Nuevos (muestra):</b> {preview.new_samples.join(" · ")}
              </div>
            )}
            {preview.updated_samples?.length > 0 && (
              <div className="text-xs text-slate-500">
                <b className="text-amber-700">Actualizados (muestra):</b>{" "}
                {preview.updated_samples.map((u) => u.name).join(" · ")}
              </div>
            )}
            {preview.conflict_samples?.length > 0 && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs space-y-1">
                <div className="flex items-center gap-1.5 font-bold text-rose-700">
                  <AlertTriangle className="h-3.5 w-3.5" /> Conflictos de identidad (irán a Revisión manual)
                </div>
                {preview.conflict_samples.map((c, i) => (
                  <div key={i}>Fila {c.row}: {c.name} — {c.reason}</div>
                ))}
              </div>
            )}
            {preview.rejected_samples?.length > 0 && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs space-y-1">
                <div className="font-bold text-rose-700">Rechazados</div>
                {preview.rejected_samples.map((r, i) => (
                  <div key={i}>Fila {r.row}: {r.reason}</div>
                ))}
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={cancel} data-testid="scouting-import-cancel-btn">
                Cancelar
              </Button>
              <Button
                onClick={confirm}
                disabled={confirming}
                className="bg-purple-700 hover:bg-purple-800 text-white font-bold"
                data-testid="scouting-import-confirm-btn"
              >
                {confirming
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Aplicando...</>
                  : <><CheckCircle2 className="h-4 w-4 mr-2" /> Confirmar importación</>}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScoutingImportDialog;
